import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { v4 as createGuid } from 'uuid';

import { ISpecOfferLabel } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers/dto/ISpecOffer';

import { H4 } from '@r1-frontend/ui-react/components/typography/heading';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';
import { COLORS } from '@r1-frontend/ui-react/tokens/colors';

import { EExpandShopItems, EShopCategory, getShopItems, IShopItem } from '~/src/api/content/shop/getShopItems';
import { getManiaReward } from '~/src/api/promo';
import ManiaPopup from '~/src/components/Promo/popups/ManiaPopup';
import PartnerPopup from '~/src/components/Promo/popups/PartnerPopup';
import { IPromoItem } from '~/src/components/Promo/popups/PartnerPopup/types';
import { PersonalOffersPopup } from '~/src/components/Promo/popups/PersonalOffersPopup';
import PromoContainer from '~/src/components/Promo/PromoContainer';
import { TYPES_POPUP } from '~/src/components/Promo/PromoContainer/constants';
import { TManiaRewards } from '~/src/components/Promo/PromoContainer/types';
import { SegmentedBannerGrid } from '~/src/components/SegmentedBanner/SegmentedBannerGrid';
import * as getFunctions from '~/src/features/promo/utils';
import SpecialOffersContainer from '~/src/features/specialOffersContainer';
import { checkMobilePlank } from '~/src/helpers/checkMobilePlank';
import { createSendVasGA } from '~/src/helpers/eventsForDataLayer';
import { useScroll } from '~/src/helpers/hooks/useScroll';
import { TState } from '~/src/store';
import { selectAccessToken } from '~/src/store/selectors/auth/selectAccessToken';
import { selectIsAuth } from '~/src/store/selectors/auth/selectIsAuth';
import { getCurrentCity } from '~/src/store/selectors/city';
import selectProviderId from '~/src/store/selectors/selectProviderId';

import * as ST from './styled';

interface IPromoProps {
    include?: Array<'mania' | 'discounts' | 'services'>,
}

type TPromoShopItem = IShopItem & {
    billing_id?: number,
    requestStopDate?: string,
    active_to?: string,
    background_color?: string,
} & Pick<IPromoItem, 'stepId' | 'colorLabels'>;

const createLabels = ({ colorLabels, stepId }: TPromoShopItem): ISpecOfferLabel[] => {
    const stepIdNew = 1348;

    /**
     * Если оффер не новый - лейблы не нужны
     */
    if (stepId !== stepIdNew) {
        return [];
    }

    return [
        ...(colorLabels || []),
        {
            type: 'new',
            color: COLORS.LabelsBg_5,
            textColor: COLORS.LabelsText_5,
            text: 'новое',
        },
    ];
};

const promoMapper = (item: IShopItem): IPromoItem => {
    const shopItem = item as TPromoShopItem;
    let activeToKey: 'requestStopDate' | 'active_to' = 'requestStopDate';
    let idKey: 'id' | 'billing_id' = 'id';

    if ('billing_id' in shopItem) {
        activeToKey = 'active_to';
        idKey = 'billing_id';
    }

    const activeToValue = shopItem[activeToKey];
    const activeTo = activeToValue
        ? `Действует до ${dayjs(activeToValue).format('DD.MM.YYYY')}`
        : 'Бессрочно';
    const id = shopItem[idKey] ?? 0;

    return {
        ...shopItem,
        isShop: 1,
        backgroundColor: shopItem.background_color,
        colorLabels: createLabels(shopItem),
        activeTo,
        id,
    };
};

const Promo = ({
    include = ['discounts', 'mania', 'services'],
}: IPromoProps) => {
    const { isAuth, accessToken, city, providerId } = useSelector((state: TState)=>({
        isAuth: selectIsAuth(state),
        accessToken: selectAccessToken(state),
        city: getCurrentCity(state),
        providerId: selectProviderId(state),
    }));

    const [isShowMobilePlank, setIsShowMobilePlank] = useState(false);
    const [reward, setReward] = useState<number | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [discounts, setDiscounts] = useState<Array<IPromoItem>>([]);
    const [additionalServices, setAdditionalServices] = useState<Array<IPromoItem>>([]);
    const [maniaRewards, setManiaRewards] = useState<{ items?: TManiaRewards[] }>({ items: [] });
    const {
        query: {
            offerId: queryOfferId,
            offer_id,
        },
        asPath,
    } = useRouter();
    const partnerRef = useRef<HTMLDivElement>(null);
    const partnerWrapperRef = useRef<HTMLDivElement>(null);
    const scrollAnchor = useScroll();

    const offerId = useMemo<string>(
        () => String(queryOfferId || offer_id),
        [queryOfferId, offer_id],
    );

    const updateReward = useCallback(() => {
        if (!maniaRewards?.items?.length) {
            return;
        }

        const maniaReward = maniaRewards.items.find(
            ({ city_id }) => city_id === city.cityId,
        )?.dealers_compensation;

        maniaReward && setReward(maniaReward);
    }, [maniaRewards, city.cityId]);

    useEffect(() => {
        (async() => {
            /**
             * TODO Логику загрузки/обработки данных
             * прибрать в стор
             */

            const [shopItems, { data: maniaRewardsPayload }] = await Promise.all([
                getShopItems(
                    providerId,
                    accessToken || null,
                    `${EShopCategory.PartnerServices},${EShopCategory.BonusToAll}`,
                    [EExpandShopItems.Category, EExpandShopItems.Labels, EExpandShopItems.Advertising],
                ),
                include.includes('mania')
                    ? getManiaReward(providerId)
                    : Promise.resolve({ data: { items: [] } }),
            ]);
            const filterPromo = (category: keyof typeof EShopCategory) =>
                shopItems
                    ?.filter((item) => item.category?.name === EShopCategory[category])
                    .map(promoMapper);

            include.includes('services') && setAdditionalServices(
                filterPromo('PartnerServices'),
            );
            setDiscounts(
                filterPromo('BonusToAll'),
            );
            setManiaRewards(maniaRewardsPayload);
            setIsLoading(false);
        })();
    }, [city, accessToken, providerId]);

    useEffect(() => {
        setIsShowMobilePlank(
            checkMobilePlank(isAuth ?? false),
        );

        if (maniaRewards && !reward) {
            updateReward();
        }
    }, [isAuth, maniaRewards, reward, updateReward]);

    /**
     * Перед скроллом к элементу, нужно дождаться пока он появится, поэтому подписываемся
     * на изменения с помощью MutationObserver.
     * TODO: стоит рассмотреть появление нового хука для асинхронного скролла
     **/
    useEffect(() => {
        if (location.hash === '#partner' && !isLoading && partnerWrapperRef.current) {
            const observerCallback = () => {
                scrollAnchor(partnerRef);
                observer.disconnect();
            };

            const observer = new MutationObserver(observerCallback);

            observer.observe(partnerWrapperRef.current, {
                childList: true,
                subtree: true,
            });

            return () => observer.disconnect();
        }
    }, [isLoading, scrollAnchor]);

    const sections = useMemo(() => {
        const result: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>[] = [];

        if (discounts.length) {
            result.push((
                <section key={createGuid()} data-test='promo-tab-discounts'>
                    {isAuth && <H4 as='h2'>Новинки и премьеры</H4>}
                    <PromoContainer
                        popupProps={{
                            reward,
                            offers: discounts,
                            offerPopupId: offerId,
                            accessToken,
                            sendVasGA: createSendVasGA('discounts: '),
                        }}
                        getFunctions={getFunctions}
                        maniaRewards={maniaRewards?.items}
                        getManiaCardData={getFunctions.getManiaCardData}
                        renderPopup={maniaRewards?.items ? ManiaPopup : PersonalOffersPopup}
                        isLoading={isLoading}
                    />
                </section>
            ));
        }

        if (additionalServices.length) {
            result.push((
                <section key={createGuid()} data-test='promo-tab-partners' ref={partnerRef}>
                    <H4 as='h2'>Предложения от партнеров</H4>
                    <PromoContainer
                        popupProps={{
                            offers: additionalServices,
                            offerPopupId: offerId,
                            accessToken,
                            returnUrl: '/promo?offersType=additional',
                            isShowMobilePlank,
                            sendVasGA: createSendVasGA('partner services:'),
                        }}
                        getFunctions={getFunctions}
                        // @ts-ignore
                        // TODO Разобраться с типами
                        renderPopup={PartnerPopup}
                        type={TYPES_POPUP.partners}
                        isLoading={isLoading}
                    />
                </section>
            ));
        }

        return result;
    }, [
        accessToken,
        city.domain,
        isShowMobilePlank,
        offerId,
        providerId,
        isLoading,
    ]);

    const specOffers = useMemo(
        () => isAuth && accessToken
            ? (
                <section key={createGuid()}>
                    <SpecialOffersContainer title={'Только для вас'} root={'promo'}/>
                </section>
            )
            : null,
        [isAuth, accessToken],
    );

    if (!specOffers && !sections.length) {
        return null;
    }

    return (
        <FullWidthListContainer $size={0} $direction='column' $wrap='nowrap'>
            <ST.Header as={asPath === '/promo' ? 'h1' : 'h2'}>Бонусы и новые предложения</ST.Header>
            <ST.Sections ref={partnerWrapperRef}>
                {specOffers}
                <SegmentedBannerGrid alias={'promo'} margin='0 0 64px 0'/>
                {sections}
            </ST.Sections>
        </FullWidthListContainer>
    );
};

export default Promo;
